import React from 'react';
import { Layout } from '../components/layout/Layout';
import { Calendar, Users, FileText, Cloud, Lock, BarChart, Check, X, ShieldCheck, CreditCard, MapPin, Zap } from 'lucide-react';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/Button';
export function ServicesPage() {
  const services = [{
    icon: <Calendar className="w-8 h-8 text-teal-600" data-id="element-569" />,
    title: 'Smart Scheduling',
    description: "Coordinate your team's workload with our intuitive drag-and-drop calendar system."
  }, {
    icon: <Users className="w-8 h-8 text-orange-500" data-id="element-570" />,
    title: 'Team Collaboration',
    description: 'Real-time chat, task assignment, and progress tracking for seamless teamwork.'
  }, {
    icon: <FileText className="w-8 h-8 text-teal-600" data-id="element-571" />,
    title: 'Document Management',
    description: 'Securely store, share, and edit files in the cloud with version control.'
  }, {
    icon: <Cloud className="w-8 h-8 text-orange-500" data-id="element-572" />,
    title: 'Cloud Storage',
    description: 'Access your business data from anywhere, on any device, at any time.'
  }, {
    icon: <Lock className="w-8 h-8 text-teal-600" data-id="element-573" />,
    title: 'Enterprise Security',
    description: 'Bank-grade encryption and role-based access controls to keep your data safe.'
  }, {
    icon: <BarChart className="w-8 h-8 text-orange-500" data-id="element-574" />,
    title: 'Analytics & Reporting',
    description: 'Gain insights into productivity and project status with detailed reports.'
  }];
  const pricingPlans = [{
    name: 'Silver Plan',
    price: '£99',
    period: 'per year',
    description: 'Perfect for small teams getting started',
    features: ['Access to task scheduling', 'Basic file sharing', 'User dashboard', 'Secure login', 'Email support', 'Up to 5 team members'],
    notIncluded: ['Team collaboration tools', 'Shared workspace', 'Performance tracking', 'Priority support'],
    paymentLink: 'https://buy.stripe.com/5kQ4gy33WgjpeFTdzaeME00',
    imageGradient: 'from-slate-400 to-slate-600',
    highlighted: false
  }, {
    name: 'Gold Plan',
    price: '£120',
    period: 'per year',
    description: 'Best for growing teams',
    features: ['Everything in Silver', 'Team collaboration tools', 'Shared workspace', 'Performance tracking', 'Advanced analytics', 'Priority support', 'Up to 15 team members', 'Custom integrations'],
    notIncluded: [],
    paymentLink: 'https://buy.stripe.com/9B63cu480fflapD2UweME01',
    imageGradient: 'from-amber-400 to-orange-600',
    highlighted: true
  }];
  const comparisonFeatures = [{
    name: 'Task scheduling',
    silver: true,
    gold: true
  }, {
    name: 'Basic file sharing',
    silver: true,
    gold: true
  }, {
    name: 'User dashboard',
    silver: true,
    gold: true
  }, {
    name: 'Secure login',
    silver: true,
    gold: true
  }, {
    name: 'Team members',
    silver: '5',
    gold: '15'
  }, {
    name: 'Team collaboration tools',
    silver: false,
    gold: true
  }, {
    name: 'Shared workspace',
    silver: false,
    gold: true
  }, {
    name: 'Performance tracking',
    silver: false,
    gold: true
  }, {
    name: 'Advanced analytics',
    silver: false,
    gold: true
  }, {
    name: 'Support level',
    silver: 'Email',
    gold: 'Priority'
  }, {
    name: 'Custom integrations',
    silver: false,
    gold: true
  }];
  return <Layout data-id="element-575">
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-slate-50 to-teal-50 py-16" data-id="element-576">
        <div className="container mx-auto px-4 text-center" data-id="element-577">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} data-id="element-578">
            <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4" data-id="element-579">
              Service Details & Pricing
            </h1>
            <p className="text-slate-600 max-w-3xl mx-auto text-lg leading-relaxed" data-id="element-580">
              HomeLinkPro offers subscription-based access to digital business
              management tools. Choose the plan that fits your team's needs and
              start collaborating today.
            </p>
          </motion.div>
        </div>
      </div>

      {/* Features Overview */}
      <div className="container mx-auto px-4 py-20" data-id="element-581">
        <div className="text-center mb-12" data-id="element-582">
          <h2 className="text-3xl font-bold text-slate-900 mb-4" data-id="element-583">
            What's Included
          </h2>
          <p className="text-slate-600 max-w-2xl mx-auto" data-id="element-584">
            Powerful tools to help your business operate efficiently
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-585">
          {services.map((service, index) => <motion.div key={index} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: index * 0.1
        }} className="p-8 rounded-xl border border-slate-100 shadow-sm bg-white" data-id="element-586">
              <div className="mb-6 p-3 bg-slate-50 rounded-lg inline-block" data-id="element-587">
                {service.icon}
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3" data-id="element-588">
                {service.title}
              </h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-589">
                {service.description}
              </p>
            </motion.div>)}
        </div>
      </div>

      {/* Pricing Plans */}
      <div className="bg-slate-50 py-20" data-id="element-590">
        <div className="container mx-auto px-4" data-id="element-591">
          <div className="text-center mb-12" data-id="element-592">
            <h2 className="text-3xl font-bold text-slate-900 mb-4" data-id="element-593">
              Choose Your Plan
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto" data-id="element-594">
              Flexible annual subscriptions with no hidden fees
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto" data-id="element-595">
            {pricingPlans.map((plan, index) => <motion.div key={index} initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6,
            delay: index * 0.2
          }} className={`relative bg-white rounded-2xl shadow-lg overflow-hidden ${plan.highlighted ? 'ring-2 ring-teal-600' : ''}`} data-id="element-596">
                {plan.highlighted && <div className="absolute top-0 right-0 bg-teal-600 text-white px-4 py-1 text-sm font-semibold rounded-bl-lg" data-id="element-597">
                    Recommended
                  </div>}

                {/* Image Placeholder */}
                <div className={`h-48 bg-gradient-to-br ${plan.imageGradient} flex items-center justify-center`} data-id="element-598">
                  <div className="text-white text-center" data-id="element-599">
                    <div className="text-6xl font-bold opacity-20" data-id="element-600">
                      {plan.name.split(' ')[0]}
                    </div>
                  </div>
                </div>

                <div className="p-8" data-id="element-601">
                  <h3 className="text-2xl font-bold text-slate-900 mb-2" data-id="element-602">
                    {plan.name}
                  </h3>
                  <p className="text-slate-600 mb-6" data-id="element-603">{plan.description}</p>

                  <div className="mb-6" data-id="element-604">
                    <span className="text-4xl font-bold text-slate-900" data-id="element-605">
                      {plan.price}
                    </span>
                    <span className="text-slate-600 ml-2" data-id="element-606">{plan.period}</span>
                  </div>

                  <a href={plan.paymentLink} target="_blank" rel="noopener noreferrer" className="block mb-6" data-id="element-607">
                    <Button className="w-full" variant={plan.highlighted ? 'primary' : 'outline'} size="lg" data-id="element-608">
                      Buy Now
                    </Button>
                  </a>

                  <div className="space-y-3" data-id="element-609">
                    {plan.features.map((feature, idx) => <div key={idx} className="flex items-start gap-3" data-id="element-610">
                        <Check className="w-5 h-5 text-teal-600 flex-shrink-0 mt-0.5" data-id="element-611" />
                        <span className="text-slate-700" data-id="element-612">{feature}</span>
                      </div>)}
                    {plan.notIncluded.map((feature, idx) => <div key={idx} className="flex items-start gap-3 opacity-40" data-id="element-613">
                        <X className="w-5 h-5 text-slate-400 flex-shrink-0 mt-0.5" data-id="element-614" />
                        <span className="text-slate-500" data-id="element-615">{feature}</span>
                      </div>)}
                  </div>
                </div>
              </motion.div>)}
          </div>
        </div>
      </div>

      {/* Comparison Table */}
      <div className="container mx-auto px-4 py-20" data-id="element-616">
        <div className="text-center mb-12" data-id="element-617">
          <h2 className="text-3xl font-bold text-slate-900 mb-4" data-id="element-618">
            Compare Plans
          </h2>
          <p className="text-slate-600 max-w-2xl mx-auto" data-id="element-619">
            See exactly what's included in each subscription
          </p>
        </div>

        <div className="max-w-4xl mx-auto overflow-x-auto" data-id="element-620">
          <table className="w-full bg-white rounded-xl shadow-sm border border-slate-200" data-id="element-621">
            <thead data-id="element-622">
              <tr className="border-b border-slate-200" data-id="element-623">
                <th className="text-left p-6 font-semibold text-slate-900" data-id="element-624">
                  Feature
                </th>
                <th className="text-center p-6 font-semibold text-slate-900" data-id="element-625">
                  Silver Plan
                </th>
                <th className="text-center p-6 font-semibold text-slate-900 bg-teal-50" data-id="element-626">
                  Gold Plan
                </th>
              </tr>
            </thead>
            <tbody data-id="element-627">
              {comparisonFeatures.map((feature, index) => <tr key={index} className="border-b border-slate-100 last:border-0" data-id="element-628">
                  <td className="p-6 text-slate-700" data-id="element-629">{feature.name}</td>
                  <td className="p-6 text-center" data-id="element-630">
                    {typeof feature.silver === 'boolean' ? feature.silver ? <Check className="w-5 h-5 text-teal-600 mx-auto" data-id="element-631" /> : <X className="w-5 h-5 text-slate-300 mx-auto" data-id="element-632" /> : <span className="text-slate-700" data-id="element-633">{feature.silver}</span>}
                  </td>
                  <td className="p-6 text-center bg-teal-50/50" data-id="element-634">
                    {typeof feature.gold === 'boolean' ? feature.gold ? <Check className="w-5 h-5 text-teal-600 mx-auto" data-id="element-635" /> : <X className="w-5 h-5 text-slate-300 mx-auto" data-id="element-636" /> : <span className="text-slate-700 font-medium" data-id="element-637">
                        {feature.gold}
                      </span>}
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
      </div>

      {/* Trust Badges */}
      <div className="bg-slate-50 py-16" data-id="element-638">
        <div className="container mx-auto px-4" data-id="element-639">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto" data-id="element-640">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} className="flex flex-col items-center text-center p-6 bg-white rounded-xl border border-slate-100 shadow-sm" data-id="element-641">
              <MapPin className="w-8 h-8 text-teal-600 mb-3" data-id="element-642" />
              <span className="font-semibold text-slate-900" data-id="element-643">UK-Based</span>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.1
          }} className="flex flex-col items-center text-center p-6 bg-white rounded-xl border border-slate-100 shadow-sm" data-id="element-644">
              <ShieldCheck className="w-8 h-8 text-teal-600 mb-3" data-id="element-645" />
              <span className="font-semibold text-slate-900" data-id="element-646">
                Secure Payments
              </span>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.2
          }} className="flex flex-col items-center text-center p-6 bg-white rounded-xl border border-slate-100 shadow-sm" data-id="element-647">
              <CreditCard className="w-8 h-8 text-teal-600 mb-3" data-id="element-648" />
              <span className="font-semibold text-slate-900" data-id="element-649">
                Clear Pricing
              </span>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.3
          }} className="flex flex-col items-center text-center p-6 bg-white rounded-xl border border-slate-100 shadow-sm" data-id="element-650">
              <Zap className="w-8 h-8 text-teal-600 mb-3" data-id="element-651" />
              <span className="font-semibold text-slate-900" data-id="element-652">
                Reliable Access
              </span>
            </motion.div>
          </div>
        </div>
      </div>
    </Layout>;
}